import GtrSuper from '@/modules/common/components/mixins/gtr-super.mixin'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { mapState } from 'vuex'
import Container from 'typedi'
import SecurityContext from '@/modules/common/services/security-context.service'
import { AccessLevel } from '@/modules/common/enums/access-levels.enum'
import GtrModuleIcon from '@/modules/common/components/ui-core/gtr-module-icon/gtr-module-icon.vue'

// Mirrors the identifier→route-pattern mapping in gtr-module-menu-item.ts
const MODULE_ROUTE_PATTERN: Record<string, string> = {
  OVERVIEW: 'event',
  SUPPORT: 'modules.support',
  EXHIBITORS: 'modules.exhibits',
  CALL_FOR_PAPERS: 'modules.call_4_papers',
  REGISTRATION: 'modules.registration',
  SURVEYS: 'modules.evals',
  SESSION_TRACKING: 'modules.track',
  LEADS: 'modules.leads',
  BADGES: 'modules.badges',
  HOTELS: 'modules.hotels'
}

export interface FamiliarNavItem {
  id: string
  label: string
  icon: string
  svgModule: string
  route: any
  children: FamiliarNavItem[]
  show: boolean
  isEnabled: boolean
  routePattern: string
}

@Component({
  name: 'GtrFamiliarUiNav',
  components: { GtrModuleIcon },
  computed: {
    ...mapState('module', ['activatedEventModules'])
  }
})
export default class GtrFamiliarUiNav extends GtrSuper {
  @Prop({ type: Array, required: true }) readonly items!: any[]
  @Prop({ type: String, default: 'company' }) readonly context!: 'company' | 'event'
  @Prop({ type: Boolean, default: false }) readonly value!: boolean

  activatedEventModules!: Record<string, any>

  collapsed = false
  openItemId: string | null = null
  hoveredItemId: string | null = null

  get isMobile (): boolean {
    return this.$vuetify.breakpoint.xsOnly
  }

  get isCollapsedRail (): boolean {
    return this.collapsed && !this.isMobile
  }

  get isExpanded (): boolean {
    return !this.isCollapsedRail
  }

  get adminLinks (): any[] {
    return this.adminDrawerMenuItems.filter((item: any) => {
      const levels = item.route.meta?.access_level
      return !levels || (this as any).$hasRoles(levels)
    })
  }

  onDrawerInput (open: boolean): void {
    if (this.isMobile) this.$emit('input', open)
  }

  get normalizedItems (): FamiliarNavItem[] {
    return this.context === 'event'
      ? this.normalizeEventItems()
      : this.normalizeCompanyItems()
  }

  private normalizeCompanyItems (): FamiliarNavItem[] {
    return (this.items || []).map((item: any) => ({
      id: item.text,
      label: item.text,
      icon: item.icon || '',
      svgModule: '',
      route: item.route ? { name: item.route.name, params: item.route.params } : null,
      children: [],
      show: item.show !== false,
      isEnabled: true,
      routePattern: item.route?.name || ''
    }))
  }

  private normalizeEventItems (): FamiliarNavItem[] {
    return (this.items || [])
      .filter((item: any) => item && item.ready && (!item.roles || (this as any).$hasRoles(item.roles)))
      .map((item: any) => {
        const children: FamiliarNavItem[] = (item.submodules || [])
          .filter((sub: any) => sub.active && this.subHasPermission(sub) && this.subHasRole(sub) && this.subModuleGateActive(sub))
          .map((sub: any) => ({
            id: sub.name,
            label: sub.name,
            icon: sub.icon || '',
            route: sub.route ? { name: sub.route.name } : null,
            children: [],
            show: true,
            isEnabled: true,
            routePattern: sub.route?.name || ''
          }))

        const isEnabled =
          item.identifier === 'OVERVIEW' ||
          item.identifier === 'SUPPORT' ||
          !!(this.activatedEventModules?.[item.identifier]?.enabled)

        return {
          id: item.identifier || item.name,
          label: item.name || item.text || '',
          icon: item.icon || '',
          svgModule: item.icon ? '' : (item.identifier || ''),
          route: item.route ? { name: item.route.name, params: item.route.params } : null,
          children,
          show: true,
          isEnabled,
          routePattern: MODULE_ROUTE_PATTERN[item.identifier] || 'event'
        }
      })
  }

  private subHasPermission (sub: any): boolean {
    const perms = sub.route?.meta?.permissions
    if (!perms) return true
    const userPerms = Container.get(SecurityContext).permissions()
    return Array.isArray(perms)
      ? perms.some((p: string) => userPerms?.[p])
      : !!userPerms?.[perms]
  }

  private subHasRole (sub: any): boolean {
    if (!sub.roles?.length) return true
    return (this as any).$hasRoles(sub.roles)
  }

  private subModuleGateActive (sub: any): boolean {
    if (!sub.moduleGate) return true
    return !!(this.activatedEventModules?.[sub.moduleGate]?.enabled)
  }

  get backConfig (): { label: string; route: any } | null {
    if (this.context === 'event') {
      return {
        label: 'Events',
        route: { name: 'level-two.company.events', params: { uuid: this.$route.params.uuid } }
      }
    }
    if (this.context === 'company' && (this as any).$hasRoles([AccessLevel.SUPER_ADMIN])) {
      return {
        label: 'Companies',
        route: { name: 'level-one.companies' }
      }
    }
    return null
  }

  goBack (): void {
    if (this.backConfig) this.$router.push(this.backConfig.route)
  }

  toggleCollapsed (): void {
    this.collapsed = !this.collapsed
    if (this.collapsed) this.openItemId = null
  }

  handleL1Click (item: FamiliarNavItem): void {
    if (!item.isEnabled) return
    this.openItemId = this.openItemId === item.id ? null : item.id
  }

  // Expandable L1: checks if the current route is within this module's section
  isL1Active (item: FamiliarNavItem): boolean {
    if (this.context === 'event') {
      return this.$route.name?.includes(`level-two.${item.routePattern}`) ?? false
    }
    return this.$route.name === item.routePattern
  }

  // Direct-link L1: exact route name match
  isL1ActiveRoute (item: FamiliarNavItem): boolean {
    return item.route ? this.$route.name === item.route.name : false
  }

  // L2 / L3: exact route name match
  isChildActive (child: FamiliarNavItem): boolean {
    return child.route ? this.$route.name === child.routePattern : false
  }

  // Merge current route params (uuid, event_uuid) into child route so router-link works
  resolveChildRoute (child: FamiliarNavItem): any {
    if (!child.route) return null
    return { name: child.route.name, params: { ...this.$route.params, ...(child.route.params || {}) } }
  }

  // MDI icons in use that have no '-outline' variant — always render filled.
  private static readonly NO_OUTLINE_ICONS = new Set([
    'mdi-barcode',
    'mdi-headset',
    'mdi-barcode-scan',
    'mdi-cellphone-check',
    'mdi-cellphone-cog',
    'mdi-chart-line-variant',
    'mdi-data-matrix-scan',
    'mdi-domain',
    'mdi-format-color-fill',
    'mdi-format-font',
    'mdi-format-list-bulleted',
    'mdi-newspaper',
    'mdi-package-variant-closed',
    'mdi-printer-settings',
    'mdi-receipt',
    'mdi-selection',
    'mdi-sync',
    'mdi-tune'
  ])

  // Outline by default, filled on hover/active.
  // Icons already provided as '-outline' are the outline variant; others have '-outline' appended.
  // Icons in NO_OUTLINE_ICONS stay filled in all states.
  getIcon (icon: string, isActive: boolean, isHovered: boolean): string {
    if (!icon) return ''
    const isAlreadyOutline = icon.endsWith('-outline')
    const filledName = isAlreadyOutline ? icon.slice(0, -8) : icon
    if (!isAlreadyOutline && GtrFamiliarUiNav.NO_OUTLINE_ICONS.has(icon)) return icon
    if (isActive || isHovered) return filledName
    return isAlreadyOutline ? icon : `${icon}-outline`
  }

  @Watch('$route')
  closeOnNavigate (): void {
    if (this.isMobile && this.value) this.$emit('input', false)
  }

  // Auto-open the L1 accordion entry whose section contains the current route.
  @Watch('$route', { immediate: true })
  syncOpenItem (): void {
    const active = this.normalizedItems.find(
      item => item.children.length && this.isL1Active(item)
    )
    if (active) this.openItemId = active.id
  }
}
